import React, { useCallback, useEffect, useState } from 'react';
import {
  Form,
  Row,
  InputNumber,
  Col,
  Switch,
  Divider,
  Card,
  Tabs,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { shouldUpdateCompare } from 'utils/utils2';
import FormInput from 'components/FormInput';
import ThirdPartyInput from 'components/ThirdPartyInput';
import ParListSelect from 'components/ParListSelect';
import Value from 'components/Value';
import TaxSelect from 'components/TaxSelect';
import GetLabel from 'DisplayFramework/components/GetLabel';
import InputCurrency from 'components/InputCurrency';
import DF from 'DisplayFramework';
import { FormInstance } from 'antd/es/form';
import { toCurrency, toRate } from 'utils/utils';
import DocList from 'pages/Document/DocList';
import moment from 'moment';
import { getFinConElementByAssetID } from 'services/finassets';
import { useQuery } from 'react-query';
import { isNil } from 'lodash';
import { getThirdPartyById } from 'services/thirdParty';

export type TabAddFinAssetFessProps = {
    objectid: number;
    object: string;
    form: FormInstance;
    currcode:string;
    entityReference: string
};
const { TabPane } = Tabs;

const TabAddFinAssetFees = (props: TabAddFinAssetFessProps) => {
  const {
    objectid,
    object,
    form,
    currcode,
    entityReference
  } = props;

  const [sendEmail,setSendEmail]=useState(false);
  
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const handleDocumentSelection = (selectedRows:any) => {
    setSelectedDocuments(selectedRows);
  };

  const { data: finComElements } = useQuery(
    ['getFinConElementByAssetID', objectid],
    () =>
      getFinConElementByAssetID({ assetid: objectid! }).then(
        (res) => res.list || []
      ),
    {
      enabled: !isNil(objectid),
    }
  );

  const recalculate = useCallback(
    (updatedValues: any) => {
      let feesoriginalnetamount = form.getFieldValue('feesoriginalnetamount');
      let feesdiscountrate = form.getFieldValue('feesdiscountrate');
      let taxrate =
        updatedValues?.taxrate !== undefined
          ? updatedValues?.taxrate
          : form.getFieldValue('taxrate');
      let feesnetamount =
        feesoriginalnetamount -
        feesoriginalnetamount * (feesdiscountrate / 100);
      let feestaxamount = feesnetamount * (taxrate / 100);
      let fessgrossamount = feesnetamount + feestaxamount;
      form.setFieldsValue({
        taxrate: taxrate,
        feesoriginalnetamount: feesoriginalnetamount,
        feesdiscountrate: feesdiscountrate,
        feesnetamount: feesnetamount,
        feestaxamount: feestaxamount,
        fessgrossamount: fessgrossamount
      });
    },
    [form]
  );

  const [tpIdSupp, setTpIDSupp] = useState(0);

  const { data: thirdParty } = useQuery(
    ['getThirdPartyById', tpIdSupp],
    () => getThirdPartyById({ tpid: tpIdSupp}).then((res) => res.data),
    {
      enabled: !isNil(tpIdSupp),
    }
  );
  
  useEffect(() => {
  
    const supplier = form.getFieldValue('supplier');

    form.setFieldsValue({
      supplier: {
        sitype: "FEES",
        sistatus: "INIT",
        ctrid: finComElements ? finComElements[0]?.ctrid : 0,
        ctreference: finComElements ? finComElements[0]?.ctreference : '',  
        sitaxtotal: form.getFieldValue('feestaxamount'),
        sinettotal: form.getFieldValue('feesnetamount'),
        // sipaymentmode: ,
        sigrosstotal: form.getFieldValue('fessgrossamount'),
        sidueamount: form.getFieldValue('fessgrossamount'),
        currcode: currcode ?? localStorage.getItem('currcode'),
        sidocdate: moment().toISOString(),
        SuppInvoiceLines: {
          sildiscountperc: form.getFieldValue('feesdiscountrate'),
          ctrid: finComElements ? finComElements[0]?.ctrid : 0,
          siltaxrate: form.getFieldValue('taxrate'),
          sildescription: form.getFieldValue('feestype'),
          silcategory: form.getFieldValue('feescategory'),
          siltype: form.getFieldValue('feestype'),
          silnettotalamount: form.getFieldValue('feesnetamount'),
          silgrosstotalamount: form.getFieldValue('fessgrossamount'),
          siltaxtotalamount: form.getFieldValue('feestaxamount'),
          assetid: objectid,
        },
        SuppInvoiceSchedules: { 
          sispaymentmode: thirdParty ? thirdParty.role.tppaymodeout : '',
          sispaymentdelaycode: thirdParty ? thirdParty.role.tppaydelayout : '',
          sistype: "PAYMENT",
          tpidbeneficiary: supplier?.tpidsupplier ?? thirdParty ? thirdParty?.generalData?.tpid : 0,
          tpbeneficiarybankaccount: thirdParty ? thirdParty.bankAccount.bkaccountnum : '',
          sisamount: form.getFieldValue('fessgrossamount'),
          sisdueamount: form.getFieldValue('fessgrossamount')
        }
      },
      thirdParty: thirdParty,
      feescategory: form.getFieldValue('feescategory'),
      status: `initial (${moment().format('YYYY-MM-DD')})`,
      DocumentList: selectedDocuments,
      event: 'tabAddFinAssetFees'
    });

  }, [form, selectedDocuments, finComElements,thirdParty]);

  return (
    <DF.Provider pagcode="tabaddfinassetfees" entity={object} entityid={objectid}>
      <Tabs animated defaultActiveKey="1" type="card">
        <TabPane tab={<GetLabel label="AssetFeesDetails" />} key="1">
          <Row gutter={16}>
            <Col span={14}>
              <Row gutter={40}>
                <FormInput
                  rules={[{ required: true }]}
                  comcode="feestype"
                  name="feestype"
                  col
                  xs={12}
                  shouldUpdate
                  labelCol={{ span: 8 }}
                  labelAlign='left'
                >
                  <ParListSelect parcode="ASSFEESTYP" />
                </FormInput>
                <Form.Item
                  rules={[{ required: true }]}
                  shouldUpdate={shouldUpdateCompare('feestype')}
                  noStyle
                >
                  {({ getFieldValue }) => (
                    <FormInput
                      comcode="status"
                      col
                      xs={12}
                      name="status"
                      rules={[{ required: true }]}
                      labelCol={{ span: 8 }}
                      labelAlign='left'
                    >
                      <Value />
                    </FormInput>
                  )}
                </Form.Item>
              </Row>
              <Row gutter={40}>
                
                <Form.Item
                  rules={[{ required: true }]}
                  shouldUpdate={shouldUpdateCompare('feestype')}
                  noStyle
                >
                  {({ getFieldValue }) => (
                    <FormInput
                      comcode="feescategory"
                      col
                      xs={12}
                      name="feescategory"
                      rules={[{ required: true }]}
                      labelCol={{ span: 8 }}
                      labelAlign='left'
                    >
                      <ParListSelect
                        parcode="ASSFEESCAT"
                        parvalueparent={getFieldValue('feestype')}
                      />
                    </FormInput>
                  )}
                </Form.Item>
                <FormInput
                  col
                  xs={12}
                  comcode="supplier"
                  rules={[{ required: true }]}
                  name="supplier"
                  labelCol={{ span: 8 }}
                  labelAlign='left'
                >
                  <ThirdPartyInput
                    role="SUPPLIER"
                    onSelected={(e)=>{
                      form.setFieldsValue({
                        supplierr: e
                      })
                      setTpIDSupp(e.tpid ?? 0);
                    }}
                  />
                </FormInput>

                <FormInput
                  col
                  xs={24}
                  name="feesdescription"
                  comcode="feesdescription"
                  rules={[{ required: true }]}
                >
                  <TextArea rows={6} />
                </FormInput>
              </Row>
            </Col>
            <Col span={10}>
              <Card type="inner" style={{ background: '#F6F3F2' }}>
                <Row gutter={16}>
                  <InputCurrency
                    col
                    md={12}
                    onChange={(value) =>
                      recalculate({ comporiginalnetamount: value })
                    }
                    currcode={currcode ?? localStorage.getItem('currcode')}
                    fieldname="feesoriginalnetamount"
                    comcode="feesoriginalnetamount"
                    rules={[{ required: true }]}
                  />
                  <FormInput
                    col
                    xs={12}
                    name="feesdiscountrate"
                    comcode="discount"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      formatter={(value) => `${value}%`}
                      onChange={(value) =>
                        recalculate({ discountRate: value })
                      }
                    />
                  </FormInput>

                  <FormInput
                    col
                    xs={12}
                    name="feesnetamount"
                    comcode="netamount"
                  >
                    <Value
                      formatter={(value) => toCurrency(value, currcode)}
                    />
                  </FormInput>
                  <FormInput
                    name="taxcode"
                    col
                    xs={12}
                    shouldUpdate
                    comcode="taxcode"
                    rules={[{ required: true }]}
                  >
                    <TaxSelect
                      taxflow="INOUT"
                      onSelected={(tax) =>
                        recalculate({ taxrate: tax.taxrate })
                      }
                    />
                  </FormInput>

                  <FormInput
                    col
                    xs={24}
                    md={12}
                    name="feestaxamount"
                    comcode="taxamount"
                  >
                    <Value
                      formatter={(value) => toCurrency(value, currcode)}
                    />
                  </FormInput>
                  <FormInput
                    col
                    xs={24}
                    md={12}
                    name="fessgrossamount"
                    comcode="grossamount"
                  >
                    <Value
                      formatter={(value) => toCurrency(value, currcode)}
                    />
                  </FormInput>
                  <FormInput
                    col
                    xs={12}
                    name="taxrate"
                    comcode="taxrate"
                    hidden
                  >
                    <Value formatter={toRate} />
                  </FormInput>
                </Row>
              </Card>
            </Col>
          </Row>
          <Divider></Divider>
          <Row gutter={16}>
            <FormInput
              col
              xs={4}
              name="flagrebilling"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </FormInput>
          </Row>
          <Row gutter={16}>
            <FormInput
              col
              xs={4}
              name="flagsendemail"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                onChange={(e) => (e === true ? setSendEmail(true) : setSendEmail(false))}
              />
            </FormInput>
          </Row>
        </TabPane>
        <TabPane tab={<GetLabel label="AssetFeesDocuments" />} key="2">
          <DocList 
            objectid={objectid}
            objecttype="ASSET"
            categorie="DOC"
            title
            applyFilter
          //showAsModal={display}
            objectreference={entityReference}
            rowSelection={{
              onChange: (_: any, selectedRows: any) => handleDocumentSelection(selectedRows)
            }}
            setSelectedDocuments={setSelectedDocuments}
          />
        </TabPane>
      </Tabs>
    </DF.Provider>
  );
};

export default TabAddFinAssetFees;
